import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Fade, Tooltip } from '@mui/material'
import _ from 'lodash'
import { GanttCellRendererProps } from './index'
import GanttDate from './ganttDate'
import { CalendarDateVO } from '../../../../../../../../../domain/value-object/CalendarDateVO'
import objects from '../../../../../../../../../utils/objects'
import { useGanttIndex } from './useGanttIndex'
import { IRowNode } from 'ag-grid-community'
import { GanttDisplayOptions } from '../../../../../../../../../domain/value-object/GanttParameterVO'
import { ProjectPlanCumulation } from '../../../../../../../../../lib/functions/projectPlan'
import {
  AggregateField,
  AggregateTarget,
  WbsItemType,
} from '../../../../../../../../../domain/entity/WbsItemEntity'
import { Progress } from '../../../../../../../../pages/ProjectPlanNew/gridOptions/cumulation'
import {
  SliderWrapper,
  ProgressGauge,
  InsideLabel,
  RightLabel,
  LeftLabel,
  BarHoverArea,
  SummaryProgressRate,
  NameLabel,
  ProgressRateLabel,
  StyledSlider,
  StyledSliderThumb,
} from './ganttSliderStyles'
import { determineLabelMode, WbsItemForDisplay } from './useLabelMode'
import { useScrollTracking } from './useScrollTracking'
import { isGanttSummaryType } from '../ganttUtil'
import {
  GanttLabelContent,
  hasAnyContent,
  buildHoverText,
} from './GanttLabelContent'
import { useDependencyCascadeSlide } from './GanttDependency/useDependencyCascadeSlide'

const startEditing = (row: IRowNode) => {
  if (!row.id) return
  const ganttCell = document.getElementById(
    `sevend-ag-cell-gantt-chart-${row.id}`
  )
  if (!ganttCell) return
  ganttCell.classList.add('sevend-ag-cell-gantt-chart-editing')
}
const finishEditing = (row: IRowNode) => {
  if (!row.id) return
  const ganttCell = document.getElementById(
    `sevend-ag-cell-gantt-chart-${row.id}`
  )
  if (!ganttCell) return
  ganttCell.classList.remove('sevend-ag-cell-gantt-chart-editing')
}
export interface GanttSliderProps extends GanttCellRendererProps {
  displayOptions?: GanttDisplayOptions
  aggregateField?: AggregateField
  aggregateTarget?: AggregateTarget
  cumulation?: ProjectPlanCumulation
  overrunLeft?: number
  overrunWidth?: number
  wbsItemForDisplay?: WbsItemForDisplay
}

export const calcProgressPositionPercent = (
  scheduledStartDate: Date,
  scheduledEndDate: Date,
  progressRate: number,
  timeScale: CalendarDateVO[]
): number => {
  if (timeScale.length === 0) return 0
  const startMs = scheduledStartDate.getTime()
  const DAY_MS = 86_400_000
  const endMs = scheduledEndDate.getTime() + DAY_MS
  if (endMs <= startMs) return 0

  const progressMs = startMs + (endMs - startMs) * progressRate
  const firstMs = timeScale[0].date.getTime()
  const lastMs = timeScale[timeScale.length - 1].date.getTime()
  const unitWidth = 100 / timeScale.length

  if (progressMs < firstMs) {
    const interval =
      timeScale.length > 1 ? timeScale[1].date.getTime() - firstMs : DAY_MS
    return ((progressMs - firstMs) / interval) * unitWidth
  }

  if (progressMs > lastMs) {
    const interval =
      timeScale.length > 1
        ? lastMs - timeScale[timeScale.length - 2].date.getTime()
        : DAY_MS
    return (timeScale.length - 1 + (progressMs - lastMs) / interval) * unitWidth
  }

  for (let i = 0; i < timeScale.length; i++) {
    const dateMs = timeScale[i].date.getTime()
    if (progressMs <= dateMs) {
      if (i === 0) return 0
      const prevMs = timeScale[i - 1].date.getTime()
      const fraction =
        dateMs === prevMs ? 0 : (progressMs - prevMs) / (dateMs - prevMs)
      return (i - 1 + fraction) * unitWidth
    }
  }
  return (timeScale.length - 1) * unitWidth
}

export const GanttSlider = (props: GanttSliderProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const innerLabelRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [cellWidth, setCellWidth] = useState(0)
  useEffect(() => {
    const el = wrapperRef.current
    if (!el) return
    setCellWidth(el.offsetWidth)
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setCellWidth(entry.contentRect.width)
      }
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  const { cascade } = useDependencyCascadeSlide({
    api: props.api,
    timeScale: props.timeScale,
    dataPath: props.dataPath ?? '',
  })
  const dataPath = props.dataPath ? props.dataPath + '.' : ''
  const getWbsItem = (data?: Record<string, unknown>) => {
    return objects.getValue(data ?? props.data, `${dataPath}wbsItem`)
  }

  const getTargetNodes = useCallback(
    () =>
      props.customEditTargetNodeSelection?.getTargetNodes(props) ||
      props.api?.getSelectedNodes(),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- propsオブジェクト全体を指定しつつ個別プロパティも明示。AG Grid ICellRendererParamsの参照は安定
    [props, props.api, props.customEditTargetNodeSelection?.getTargetNodes]
  )

  const startEditingTargetRows = useCallback(() => {
    const selectedRows = getTargetNodes()
    if (selectedRows && selectedRows.length > 0) {
      selectedRows.forEach(row => startEditing(row))
    } else {
      startEditing(props.node)
    }
    return selectedRows
  }, [getTargetNodes, props.node])

  const ganttDate = useMemo(
    () =>
      new GanttDate({
        scale: props.timeScale,
        dateTerm: getWbsItem().scheduledDate,
      }),
    [props.value, props.parameter]
  )

  const aggregateField =
    props.aggregateField ?? AggregateField.WBS_ITEM_WORKLOAD
  const aggregateTarget = props.aggregateTarget ?? WbsItemType.TASK
  const { progressRate, hasChildrenToAggregate } = useMemo(() => {
    if (!props.isV2 || !props.cumulation) {
      return { progressRate: 0, hasChildrenToAggregate: false }
    }
    const progress = new Progress(
      props.cumulation,
      aggregateTarget,
      aggregateField
    )
    return {
      progressRate: progress.progressRate ?? 0,
      hasChildrenToAggregate: progress.total > 0,
    }
  }, [props.isV2, props.cumulation, aggregateField, aggregateTarget])

  const progressEndPercent = useMemo(() => {
    if (progressRate <= 0) return 0
    return calcProgressPositionPercent(
      ganttDate.startDate.date,
      ganttDate.endDate.date,
      progressRate,
      props.timeScale
    )
  }, [ganttDate.startDate, ganttDate.endDate, progressRate, props.timeScale])

  const ganttIndex = useGanttIndex(ganttDate)
  const sliderValue = useRef<number[]>([])
  sliderValue.current = ganttIndex.value

  useEffect(() => {
    if (getWbsItem().scheduledDate) {
      ganttIndex.initialize(ganttDate.startIndex, ganttDate.endIndex)
    }
  }, [props.value, props.parameter])

  const onEdit = useCallback(
    (enableCascade: boolean) => {
      if (!props.editable) return
      const movedRows = ganttIndex.getMovingRows()
      try {
        if (movedRows.length > 0) {
          movedRows.forEach(row => finishEditing(row))
        } else {
          finishEditing(props.node)
        }

        const wbsItemBefore = getWbsItem()
        const oldDate = {
          startDate: wbsItemBefore?.scheduledDate?.startDate
            ? new Date(wbsItemBefore.scheduledDate.startDate)
            : null,
          endDate: wbsItemBefore?.scheduledDate?.endDate
            ? new Date(wbsItemBefore.scheduledDate.endDate)
            : null,
        }

        const updated = ganttIndex.edit(_.cloneDeep(props.data), dataPath)
        if (updated) {
          const newWbsItem = objects.getValue(updated[0], `${dataPath}wbsItem`)
          const newDate = {
            startDate: newWbsItem?.scheduledDate?.startDate
              ? new Date(newWbsItem.scheduledDate.startDate)
              : null,
            endDate: newWbsItem?.scheduledDate?.endDate
              ? new Date(newWbsItem.scheduledDate.endDate)
              : null,
          }

          const cascaded = enableCascade
            ? cascade(updated, oldDate, newDate)
            : []
          props.updateRows([...updated, ...cascaded])
        }
      } finally {
        props.customEditTargetNodeSelection?.rememberTargetNodes?.(movedRows)
      }
    },
    [ganttIndex.value, props.customEditTargetNodeSelection, cascade]
  )

  const onBulrSlider = useCallback(
    () => props.customEditTargetNodeSelection?.clearTargetNodes?.(),
    [props.customEditTargetNodeSelection]
  )

  const trackLeftPercent = (ganttIndex.value[0] / props.timeScale.length) * 100
  const trackRightPercent = (ganttIndex.value[1] / props.timeScale.length) * 100
  const barWidthPercent =
    Math.min(100, trackRightPercent) - Math.max(0, trackLeftPercent)
  // オーバーラン（遅延線）がある場合、right ラベルはオーバーランの右側に配置
  const barEndPercent =
    props.overrunLeft != null && props.overrunWidth != null
      ? props.overrunLeft + props.overrunWidth
      : trackRightPercent
  const isBarVisible =
    ganttIndex.value[0] < props.timeScale.length && ganttIndex.value[1] > 0

  const rightSpacePercent = 100 - barEndPercent
  const leftSpacePercent = Math.max(0, trackLeftPercent)

  const wbsItem = getWbsItem()
  const displayOptions = props.displayOptions
  const wbsItemForDisplay = props.wbsItemForDisplay
  const isSummary = isGanttSummaryType(wbsItem.wbsItemType)

  const labelMode = determineLabelMode({
    isV2: !!props.isV2,
    isBarVisible,
    isSummary,
    isDragging,
    cellWidth,
    rightSpacePercent,
  })

  useScrollTracking(wrapperRef, innerLabelRef, {
    enabled: labelMode === 'inside' && !!props.isV2,
    trackLeftPercent,
    trackRightPercent,
    displayOptions,
  })

  if (
    (props.timeScale.length < ganttDate.startIndex &&
      props.timeScale.length < ganttDate.endIndex) ||
    (ganttDate.startIndex < 0 && ganttDate.endIndex < 0)
  ) {
    return <></>
  }

  const slider = (
    <StyledSlider
      key={`gantt-slider-${props.node?.rowIndex}`}
      value={ganttIndex.value}
      size="small"
      components={{ Thumb: StyledSliderThumb }}
      valueLabelDisplay="auto"
      valueLabelFormat={(v, index) => {
        return index === 0
          ? ganttDate.getStartDateByIndex(v).format('M/D')
          : ganttDate.getEndDateByIndex(v).format('M/D')
      }}
      onBlur={onBulrSlider}
      componentsProps={
        props.editable
          ? {
              track: {
                onMouseDown: () => {
                  setIsDragging(true)
                  const selectedRows = startEditingTargetRows()
                  ganttIndex.moveWithTrack(selectedRows)
                },
              },
              thumb: {
                onMouseDown: () => {
                  setIsDragging(true)
                  const selectedRows = startEditingTargetRows()
                  ganttIndex.moveWithThumb(selectedRows)
                },
              },
            }
          : undefined
      }
      onChange={(event: Event, val: number | number[], activeThumb: number) => {
        if (!props.editable) return
        const indexes = typeof val === 'number' ? [val, val] : val
        if (event.type === 'mousedown') {
          ganttIndex.setMoveWithTrackOrigin(indexes, activeThumb)
        }
        if (event.type !== 'mousemove') return
        ganttIndex.move(indexes, activeThumb)
      }}
      onChangeCommitted={(event: React.SyntheticEvent | Event) => {
        setIsDragging(false)
        const nativeEvent = 'nativeEvent' in event ? event.nativeEvent : event
        const ctrlKey =
          nativeEvent instanceof MouseEvent &&
          (nativeEvent.ctrlKey || nativeEvent.metaKey)
        onEdit(ctrlKey)
      }}
      max={props.timeScale.length}
      wbsitemtype={wbsItem.wbsItemType}
      scheduled={
        +(
          !!wbsItem.scheduledDate?.startDate && !!wbsItem.scheduledDate?.endDate
        )
      }
      disablethumbs={[!!ganttIndex.startDisabled, !!ganttIndex.endDisabled]}
      index={props.node?.rowIndex ?? 0}
      isv2={props.isV2 ? 1 : 0}
      overrunleft={props.overrunLeft}
      overrunwidth={props.overrunWidth}
    />
  )

  if (!props.isV2) {
    return (
      <Fade in={true} timeout={300}>
        {slider}
      </Fade>
    )
  }

  const visibleLeft = Math.max(0, trackLeftPercent)

  const hasCumulation = hasChildrenToAggregate && !wbsItem.wbsItemType?.isTask()
  const labelContentProps = {
    isSummary,
    displayOptions,
    wbsItemForDisplay,
    progressRate,
    labelMode,
    hasCumulation,
  }
  const hasInfoContent = hasAnyContent(labelContentProps)
  const hoverText = buildHoverText(labelContentProps)
  const tooltipProps = {
    title: hoverText,
    placement: 'top' as const,
    arrow: true,
    componentsProps: { tooltip: { sx: { maxWidth: 450 } } },
  }

  return (
    <Fade in={true} timeout={300}>
      <SliderWrapper ref={wrapperRef}>
        {slider}
        {progressRate > 0 &&
          isBarVisible &&
          props.isV2 &&
          wbsItem.scheduledDate?.startDate &&
          wbsItem.scheduledDate?.endDate && (
            <ProgressGauge
              style={{
                left: `${Math.max(0, trackLeftPercent)}%`,
                width: `${Math.max(0, Math.min(100, progressEndPercent) - Math.max(0, trackLeftPercent))}%`,
              }}
              barHeight={isSummary ? 32 : 20}
            />
          )}
        {isBarVisible && isSummary && (
          <>
            <Tooltip {...tooltipProps}>
              <BarHoverArea
                left={visibleLeft}
                width={barWidthPercent}
                isSummary={true}
              />
            </Tooltip>
            {displayOptions?.showName && wbsItemForDisplay?.displayName && (
              <Tooltip {...tooltipProps}>
                <InsideLabel
                  ref={innerLabelRef}
                  left={visibleLeft}
                  width={barWidthPercent}
                  isSummary={true}
                >
                  <NameLabel inside={true} isSummary={true}>
                    {wbsItemForDisplay.displayName}
                  </NameLabel>
                </InsideLabel>
              </Tooltip>
            )}
            {displayOptions?.showProgressRate &&
              hasCumulation &&
              (rightSpacePercent * cellWidth) / 100 >= 60 && (
                <SummaryProgressRate left={barEndPercent}>
                  <ProgressRateLabel
                    inside={false}
                    isSummary={true}
                    showSeparator={false}
                  >
                    {(progressRate * 100).toFixed(1)}%
                  </ProgressRateLabel>
                </SummaryProgressRate>
              )}
          </>
        )}
        {isBarVisible && !isSummary && hasInfoContent && (
          <Tooltip {...tooltipProps}>
            {labelMode === 'hover' ? (
              <BarHoverArea left={visibleLeft} width={barWidthPercent} />
            ) : labelMode === 'right' ? (
              <RightLabel
                left={barEndPercent}
                isSummary={false}
                maxWidth={`${rightSpacePercent}%`}
              >
                <GanttLabelContent {...labelContentProps} />
              </RightLabel>
            ) : (
              <LeftLabel
                barLeft={leftSpacePercent}
                isSummary={false}
                maxWidth={`${leftSpacePercent}%`}
              >
                <GanttLabelContent {...labelContentProps} />
              </LeftLabel>
            )}
          </Tooltip>
        )}
      </SliderWrapper>
    </Fade>
  )
}
